import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react'

/** Observe an element and report when it first scrolls into view (once). */
export function useInView<T extends HTMLElement>(rootMargin = '0px 0px -12% 0px') {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true)
          io.disconnect()
        }
      },
      { rootMargin, threshold: 0.08 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [inView, rootMargin])
  return { ref, inView }
}

/**
 * Scroll-reveal wrapper. Children fade/slide in once on first view.
 * Honors prefers-reduced-motion (the global rule collapses the transition to ~0ms,
 * so content still appears — it just snaps instead of animating).
 */
export function Reveal({
  as: As = 'div',
  delay = 0,
  className = '',
  play = true,
  rootMargin,
  children,
}: {
  as?: ElementType
  delay?: number
  className?: string
  /** Hold the reveal until true (e.g. while the boot veil is still up). */
  play?: boolean
  /**
   * Observer margin. The default shrinks the viewport 12% at the bottom — a deliberate
   * marketing aesthetic (content reveals just before you reach it). DATA rows must pass
   * '0px': with the default, a last row landing in that bottom band on a page too short
   * to scroll never intersects and stays PERMANENTLY invisible (ghost cards).
   */
  rootMargin?: string
  children: ReactNode
}) {
  const { ref, inView } = useInView<HTMLElement>(rootMargin)
  return (
    <As
      ref={ref as never}
      className={`reveal ${inView && play ? 'is-in' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </As>
  )
}
